/**
 * Process Admin table service for FlexibleCRUDTable.
 * Fetches flat process/subprocess table and supports client-side search, sort, pagination.
 */
import { benchmarkProcessesAPI, type ProcessSubprocessTableRow } from './benchmarkProcessesAPI';

export type ProcessAdminTableRow = ProcessSubprocessTableRow & { id: string };

function rowId(row: ProcessSubprocessTableRow): string {
  return row.subprocess_id ?? row.process_id;
}

function matchesSearch(row: ProcessSubprocessTableRow, search: string): boolean {
  if (!search || !search.trim()) return true;
  const q = search.trim().toLowerCase();
  const area = (row.area_name ?? '').toLowerCase();
  const process = (row.process_name ?? '').toLowerCase();
  const sub = (row.subprocess_name ?? '').toLowerCase();
  return area.includes(q) || process.includes(q) || sub.includes(q);
}

function compare(a: string | null, b: string | null, dir: 'asc' | 'desc'): number {
  const va = (a ?? '').toLowerCase();
  const vb = (b ?? '').toLowerCase();
  const c = va.localeCompare(vb);
  return dir === 'asc' ? c : -c;
}

/** Compare subprocess_name with nulls first (process-only row before its subprocesses), then asc. */
function compareSubprocess(a: string | null, b: string | null): number {
  const aEmpty = a == null || a.trim() === '';
  const bEmpty = b == null || b.trim() === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return -1;
  if (bEmpty) return 1;
  return compare(a, b, 'asc');
}

export const processAdminTableService = {
  async getAll(params: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<{ data: ProcessAdminTableRow[]; pagination: Record<string, number | boolean> }> {
    const page = Math.max(1, params.page ?? 1);
    const limit = Math.max(1, Math.min(100, params.limit ?? 25));
    const search = (params.search ?? '').trim();
    const sortBy = params.sortBy ?? 'area_name';
    const sortOrder = params.sortOrder ?? 'asc';

    const rows = await benchmarkProcessesAPI.listProcessSubprocessTable();
    let list: ProcessAdminTableRow[] = rows.map((r) => ({ ...r, id: rowId(r) }));

    if (search) {
      list = list.filter((r) => matchesSearch(r, search));
    }

    // Default: grouped by Area, then Process, then subprocess (process-only row first, then subprocesses)
    list.sort((a, b) => {
      const byArea = compare(a.area_name, b.area_name, sortBy === 'area_name' ? sortOrder : 'asc');
      if (byArea !== 0) return byArea;
      const byProcess = compare(a.process_name, b.process_name, sortBy === 'process_name' ? sortOrder : 'asc');
      if (byProcess !== 0) return byProcess;
      if (sortBy === 'subprocess_name') {
        return compare(a.subprocess_name, b.subprocess_name, sortOrder);
      }
      return compareSubprocess(a.subprocess_name, b.subprocess_name);
    });

    const total = list.length;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit;
    const data = list.slice(start, start + limit);

    return {
      data,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1
      }
    };
  },

  delete(id: string): Promise<void> {
    return benchmarkProcessesAPI.delete(id);
  }
};
